import { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Search, SlidersHorizontal, MapPin, Star, DollarSign, ChevronDown, Truck, Store, ShoppingBag, List, X, Utensils } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import FilterModal from '@/components/FilterModal';
import LocationSelectionModal from '@/components/LocationSelectionModal';
import DistanceDisplay from '@/components/DistanceDisplay';
import DistanceSelectorSheet from '@/components/DistanceSelectorSheet';
import TopHeader from '@/components/TopHeader';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAuth } from '@/hooks/useAuth';
import { useSessionLocation } from '@/hooks/useSessionLocation';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface LocalRestaurant {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  category: string | null;
  priceRange: string | null;
  overallRating: number;
  totalReviews: number;
  recentReviews: number;
  latitude: string | null;
  longitude: string | null;
  distanceKm: number | null;
  orderingEnabled: boolean;
  eatzyClientId: string | null;
  hasDelivery: boolean;
  hasTakeout: boolean;
  hasReservations: boolean;
  eatzyBannerUrl: string | null;
  eatzyDeliveryFee: string | null;
  eatzyMinimumOrder: string | null;
  eatzyDelays: { takeout?: number; delivery?: number } | null;
  friendReviewCount: number;
  tags: string[];
  reviewTags: string[];
  isOpen?: boolean;
  routeDuration?: number;
  deliveryWindow?: number;
  hoursJson?: Record<string, unknown>;
  eatzyTimezone?: string;
}

interface DeliveryInfo {
  eatzyId: number;
  profile: string;
  fees: Record<string, number>;
  delays: { takeout?: number; delivery?: number };
  windows: { takeout?: number; delivery?: number };
  routeDuration?: number;
  open: boolean;
  modes: { takeout: boolean; delivery: boolean; reservations: boolean };
}

interface DeliveryInfoResponse {
  success: boolean;
  deliveryInfo: Record<string, DeliveryInfo>;
  count: number;
  error?: string;
}

const createCustomIcon = (isOpen?: boolean, hasOrdering?: boolean) => {
  const color = hasOrdering ? (isOpen ? '#22c55e' : '#ef4444') : '#6366f1';
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      width: 32px;
      height: 32px;
      background-color: ${color};
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/>
        <path d="M7 2v20"/>
        <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>
      </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const createUserLocationIcon = () => {
  return L.divIcon({
    className: 'user-location-marker',
    html: `<div style="
      width: 20px;
      height: 20px;
      background-color: #3b82f6;
      border: 4px solid white;
      border-radius: 50%;
      box-shadow: 0 0 0 2px #3b82f6, 0 2px 6px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

function MapUpdater({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

interface RestaurantMapViewProps {
  serviceType?: 'delivery' | 'takeout';
  location?: string;
  latitude?: number;
  longitude?: number;
  timing?: 'asap' | 'scheduled';
  scheduledTime?: string;
  radius?: number;
  onEditParams?: () => void;
}

export default function RestaurantMapView({
  serviceType = 'delivery',
  location = '',
  latitude: propLatitude,
  longitude: propLongitude,
  timing = 'asap',
  scheduledTime = '',
  radius = 10,
  onEditParams
}: RestaurantMapViewProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { primaryAddress, profile: userProfile } = useUserProfile();
  
  // Fetch unread message count for header
  const { data: unreadMessageData } = useQuery<{ count: number }>({
    queryKey: ['/api/user/unread-messages-count'],
    enabled: !!user,
    refetchInterval: 60000,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'relevance' | 'rating' | 'distance' | 'reviews' | 'name'>('distance');
  const [priceFilter, setPriceFilter] = useState<string[]>([]);
  const [cuisineFilter, setCuisineFilter] = useState<string[]>([]);
  const [serviceFilter, setServiceFilter] = useState<string[]>([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [tagFilters, setTagFilters] = useState<{ include: string[]; exclude: string[] }>({ include: [], exclude: [] });
  const { selectedLocationName, customLatitude, customLongitude, setSessionLocation } = useSessionLocation();
  const [showDistanceSheet, setShowDistanceSheet] = useState(false);
  const [selectedDistance, setSelectedDistance] = useState<number | null>(10);
  const mapRef = useRef<L.Map | null>(null);

  const effectiveLatitude = customLatitude ?? propLatitude ?? primaryAddress?.latitude;
  const effectiveLongitude = customLongitude ?? propLongitude ?? primaryAddress?.longitude;
  const effectiveLocation = selectedLocationName || location || primaryAddress?.streetAddress || 'Select a location';
  const hasValidLocation = effectiveLatitude !== undefined && effectiveLongitude !== undefined;

  const handleLocationSelect = (locationData: { name: string; address: string; coordinates?: { lat: number; lng: number } } | 'current') => {
    if (locationData === 'current') {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setSessionLocation('Current Location', position.coords.latitude, position.coords.longitude);
            setShowLocationModal(false);
          },
          () => {
            toast({
              title: 'Location Error',
              description: 'Could not get your current location. Please try a saved address.',
              variant: 'destructive'
            });
          }
        );
      } else {
        toast({
          title: 'Location Error',
          description: 'Geolocation is not supported by your browser',
          variant: 'destructive'
        });
      }
    } else {
      if (locationData.coordinates) {
        setSessionLocation(locationData.name, locationData.coordinates.lat, locationData.coordinates.lng);
        setShowLocationModal(false);
      } else {
        toast({
          title: 'Address Selected',
          description: 'This address doesn\'t have coordinates for distance calculations.',
        });
        setSessionLocation(locationData.name);
        setShowLocationModal(false);
      }
    }
  };

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (effectiveLatitude) params.set('latitude', String(effectiveLatitude));
    if (effectiveLongitude) params.set('longitude', String(effectiveLongitude));
    if (priceFilter.length > 0) params.set('priceRange', priceFilter.join(','));
    if (cuisineFilter.length > 0) params.set('cuisines', cuisineFilter.join(','));
    if (serviceFilter.length > 0) params.set('services', serviceFilter.join(','));
    const radiusKm = selectedDistance !== null ? selectedDistance * 1.60934 : null;
    if (radiusKm !== null) params.set('maxDistance', String(Math.round(radiusKm)));
    params.set('sortBy', sortBy);
    if (user?.id) params.set('userId', String(user.id));
    params.set('limit', '100');
    return params.toString();
  }, [searchQuery, effectiveLatitude, effectiveLongitude, priceFilter, cuisineFilter, serviceFilter, selectedDistance, sortBy, user?.id]);

  const { data, isLoading, isError, refetch } = useQuery<{
    success: boolean;
    restaurants: LocalRestaurant[];
    total: number;
    hasMore: boolean;
  }>({
    queryKey: ['/api/restaurants/list', queryParams],
    queryFn: async () => {
      const response = await fetch(`/api/restaurants/list?${queryParams}`);
      if (!response.ok) {
        throw new Error('Failed to fetch restaurants');
      }
      return response.json();
    },
  });

  const deliveryQueryParams = useMemo(() => {
    if (!hasValidLocation) return null;
    const params = new URLSearchParams();
    params.set('lat', String(effectiveLatitude));
    params.set('lng', String(effectiveLongitude));
    params.set('mode', serviceType);
    params.set('type', timing === 'scheduled' ? 'target' : 'asap');
    return params.toString();
  }, [effectiveLatitude, effectiveLongitude, serviceType, timing, hasValidLocation]);

  const { data: deliveryData } = useQuery<DeliveryInfoResponse>({
    queryKey: ['/api/restaurants/delivery-info', deliveryQueryParams],
    queryFn: async () => {
      if (!deliveryQueryParams) return { success: false, deliveryInfo: {}, count: 0 };
      const response = await fetch(`/api/restaurants/delivery-info?${deliveryQueryParams}`);
      if (!response.ok) {
        return { success: false, deliveryInfo: {}, count: 0 };
      }
      return response.json();
    },
    enabled: hasValidLocation && !!deliveryQueryParams,
    staleTime: 60000,
  });

  const deliveryInfoMap = deliveryData?.deliveryInfo || {};

  const restaurants = useMemo(() => {
    const baseRestaurants = data?.restaurants || [];
    return baseRestaurants.map(restaurant => {
      const eatzyProfile = restaurant.eatzyClientId;
      const deliveryInfo = eatzyProfile ? deliveryInfoMap[eatzyProfile] : null;
      if (deliveryInfo) {
        return {
          ...restaurant,
          hasDelivery: deliveryInfo.modes.delivery,
          hasTakeout: deliveryInfo.modes.takeout,
          hasReservations: deliveryInfo.modes.reservations,
          eatzyDelays: deliveryInfo.delays,
          eatzyDeliveryFee: deliveryInfo.fees['Delivery Fee']?.toString() || restaurant.eatzyDeliveryFee,
          isOpen: deliveryInfo.open,
          routeDuration: deliveryInfo.routeDuration,
          deliveryWindow: deliveryInfo.windows?.delivery,
        };
      }
      return restaurant;
    });
  }, [data?.restaurants, deliveryInfoMap]);

  const filteredRestaurants = useMemo(() => {
    let result = [...restaurants];
    if (tagFilters.include.length > 0) {
      result = result.filter(r => 
        tagFilters.include.some(tag => r.tags.includes(tag.toLowerCase()))
      );
    }
    if (tagFilters.exclude.length > 0) {
      result = result.filter(r => 
        !tagFilters.exclude.some(tag => r.tags.includes(tag.toLowerCase()))
      );
    }
    return result;
  }, [restaurants, tagFilters]);

  const restaurantsWithCoords = useMemo(() => {
    return filteredRestaurants.filter(r => r.latitude && r.longitude);
  }, [filteredRestaurants]);

  const mapCenter: [number, number] = useMemo(() => {
    if (effectiveLatitude && effectiveLongitude) {
      return [effectiveLatitude, effectiveLongitude];
    }
    if (restaurantsWithCoords.length > 0) {
      const first = restaurantsWithCoords[0];
      return [parseFloat(first.latitude!), parseFloat(first.longitude!)];
    }
    return [39.7392, -104.9903]; // Denver default
  }, [effectiveLatitude, effectiveLongitude, restaurantsWithCoords]);

  const mapZoom = selectedDistance ? Math.max(10, 14 - Math.log2(selectedDistance)) : 12;

  const truncatedLocation = effectiveLocation.length > 30 
    ? effectiveLocation.substring(0, 30) + '...' 
    : effectiveLocation;

  const formatDistance = (distanceKm: number | null) => {
    if (distanceKm === null) return null;
    const miles = distanceKm * 0.621371;
    return miles < 0.1 ? '< 0.1 mi' : `${miles.toFixed(1)} mi`;
  };

  // Build user object for TopHeader
  const headerUser = user ? {
    id: String(user.id),
    name: user.name || user.email?.split('@')[0] || 'User',
    email: user.email,
    avatar: user.avatar,
    kudozBalance: (user as any).kudozBalance || 0,
  } : undefined;

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      {/* App Header */}
      <TopHeader
        user={headerUser}
        unreadAlertCount={(userProfile as any)?.unreadAlertCount || 0}
        unreadMessageCount={unreadMessageData?.count || 0}
        onNotificationsClick={() => navigate('/profile?section=alerts')}
        onMessagesClick={() => navigate('/profile?section=comments')}
        onProfileClick={() => navigate('/profile')}
      />
      
      {/* Map Controls Header */}
      <div className="bg-card/95 backdrop-blur-sm border-b border-border px-4 py-3 space-y-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLocationModal(true)}
            className="flex items-center gap-1.5 min-w-0 text-sm hover:bg-muted/50 rounded-md px-2 py-1 transition-colors"
            data-testid="button-location-select"
          >
            <MapPin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
            <span className="truncate font-medium max-w-[150px]" data-testid="text-location">{truncatedLocation}</span>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          </button>
          {hasValidLocation && (
            <DistanceDisplay
              distance={selectedDistance}
              onClick={() => setShowDistanceSheet(true)}
              data-testid="button-distance-select"
            />
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => navigate('/restaurants')}
            data-testid="button-list-view"
          >
            <List className="w-4 h-4" />
            List
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search restaurants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9"
              data-testid="input-search"
            />
          </div>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
            <SelectTrigger className="w-[120px] h-9" data-testid="select-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="distance">Distance</SelectItem>
              <SelectItem value="rating">Rating</SelectItem>
              <SelectItem value="reviews">Reviews</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="relevance">Relevance</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowFilterModal(true)}
            data-testid="button-filter"
          >
            <SlidersHorizontal className="w-4 h-4" />
          </Button>
        </div>

        {(priceFilter.length > 0 || serviceFilter.length > 0 || cuisineFilter.length > 0) && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Filters:</span>
            {priceFilter.map(p => (
              <Badge 
                key={p} 
                variant="secondary" 
                className="text-xs cursor-pointer"
                onClick={() => setPriceFilter(prev => prev.filter(x => x !== p))}
              >
                {p} <X className="w-3 h-3 ml-1" />
              </Badge>
            ))}
            {serviceFilter.map(s => (
              <Badge 
                key={s} 
                variant="secondary" 
                className="text-xs cursor-pointer"
                onClick={() => setServiceFilter(prev => prev.filter(x => x !== s))}
              >
                {s} <X className="w-3 h-3 ml-1" />
              </Badge>
            ))}
            {(priceFilter.length > 0 || serviceFilter.length > 0) && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-xs"
                onClick={() => {
                  setPriceFilter([]);
                  setServiceFilter([]);
                  setCuisineFilter([]);
                }}
              >
                Clear all
              </Button>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{restaurantsWithCoords.length} restaurants on map</span>
          {restaurantsWithCoords.length < filteredRestaurants.length && (
            <span className="text-amber-600">({filteredRestaurants.length - restaurantsWithCoords.length} without coordinates)</span>
          )}
        </div>
      </div>

      <div className="flex-1 relative pb-16">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-3">
              <Skeleton className="w-12 h-12 rounded-full mx-auto" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center py-12">
              <p className="text-destructive mb-2">Failed to load restaurants</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Try Again
              </Button>
            </div>
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: '100%', width: '100%' }}
            ref={mapRef}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapUpdater center={mapCenter} zoom={mapZoom} />
            
            {hasValidLocation && (
              <Marker
                position={[effectiveLatitude!, effectiveLongitude!]}
                icon={createUserLocationIcon()}
              >
                <Popup>
                  <div className="text-sm font-medium">Your Location</div>
                </Popup>
              </Marker>
            )}

            {restaurantsWithCoords.map((restaurant) => (
              <Marker
                key={restaurant.id}
                position={[parseFloat(restaurant.latitude!), parseFloat(restaurant.longitude!)]}
                icon={createCustomIcon(restaurant.isOpen, restaurant.orderingEnabled)}
              >
                <Popup>
                  <div className="min-w-[200px] max-w-[280px]">
                    <div className="font-semibold text-base mb-1">{restaurant.name}</div>
                    
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      {Number(restaurant.overallRating) > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                          <span>{Number(restaurant.overallRating).toFixed(1)}</span>
                        </div>
                      )}
                      {restaurant.totalReviews > 0 && (
                        <span>({restaurant.totalReviews} reviews)</span>
                      )}
                      {restaurant.priceRange && (
                        <span>{restaurant.priceRange}</span>
                      )}
                    </div>

                    {restaurant.category && (
                      <p className="text-xs text-muted-foreground mb-2">{restaurant.category}</p>
                    )}

                    <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">
                        {restaurant.address || restaurant.city || 'Location available'}
                        {restaurant.distanceKm !== null && ` · ${formatDistance(restaurant.distanceKm)}`}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 flex-wrap mb-2">
                      {restaurant.isOpen !== undefined && (
                        <Badge 
                          variant="secondary" 
                          className={cn(
                            "text-xs px-1.5 py-0",
                            restaurant.isOpen 
                              ? "bg-green-500/10 text-green-600 border-green-500/20"
                              : "bg-red-500/10 text-red-600 border-red-500/20"
                          )}
                        >
                          {restaurant.isOpen ? 'Open' : 'Closed'}
                        </Badge>
                      )}
                      {restaurant.hasDelivery && (
                        <Badge variant="outline" className="text-xs px-1.5 py-0">
                          <Truck className="w-3 h-3 mr-0.5" />
                          Delivery
                        </Badge>
                      )}
                      {restaurant.hasTakeout && (
                        <Badge variant="outline" className="text-xs px-1.5 py-0">
                          <Store className="w-3 h-3 mr-0.5" />
                          Takeout
                        </Badge>
                      )}
                    </div>

                    <Button
                      size="sm"
                      className="w-full h-8"
                      onClick={() => navigate(`/restaurant/${restaurant.id}`)}
                    >
                      {restaurant.orderingEnabled ? (
                        <>
                          <ShoppingBag className="w-3 h-3 mr-1" />
                          Order Now
                        </>
                      ) : (
                        'View Details'
                      )}
                    </Button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>


      <FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApplyFilters={(filters) => {
          setTagFilters(filters);
          setShowFilterModal(false);
        }}
        initialFilters={tagFilters}
        selectedExperienceTypes={serviceFilter.length > 0 ? serviceFilter as ('delivery' | 'takeout' | 'dine-in')[] : ['delivery', 'takeout']}
      />

      <LocationSelectionModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onLocationSelect={handleLocationSelect}
        selectedLocation={effectiveLocation}
      />

      <DistanceSelectorSheet
        isOpen={showDistanceSheet}
        onClose={() => setShowDistanceSheet(false)}
        currentDistance={selectedDistance}
        onDistanceChange={(distance) => {
          setSelectedDistance(distance);
          setShowDistanceSheet(false);
        }}
      />

    </div>
  );
}
